import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { OrderDocumentsService, Tag } from 'app/shared/services/order-documents/order-documents.service';
import { DocumentDialogData } from '../order-documents.component';
import { AutocompleteTag } from 'app/shared/components/tags-autocomplete/tags-autocomplete/tags-autocomplete.component';
import { atLeastOneTagValidator } from 'app/shared/helpers/form.helpers';
import moment from 'moment';
import { JwtAuthService } from 'app/shared/services/auth/jwt-auth.service';

const MAX_FILE_SIZE = 10000000;
@Component({
    selector: 'app-dialog-upload-document',
    templateUrl: './dialog-upload-document.component.html',
    styleUrls: ['./dialog-upload-document.component.scss'],
    standalone: false
})
export class DialogUploadDocumentComponent implements OnInit {
  uploadDocumentForm: UntypedFormGroup;
  allAvailableTags: Tag[] = [];
  loading: boolean = false;
  defaultTags: AutocompleteTag[] = [];
  uploadSuccess = false;
  verificationErrors: string[] = [];
  verificationExpectedData: any = null;
  verificationMatches: any = null;
  unsupportedFormatError: string = null;
  isAdmin: boolean = false;
  allowVerificationOverride: boolean = false;

  constructor(private formBuilder: UntypedFormBuilder,
    private alertService: AlertService,
    private documentService: OrderDocumentsService,
    @Inject(MAT_DIALOG_DATA) public data: DocumentDialogData,
    private dialogRef: MatDialogRef<DialogUploadDocumentComponent>,
    private jwtAuth: JwtAuthService
  ) { }

  ngOnInit(): void {
    this.isAdmin = this.jwtAuth.isAdmin();
    this.allAvailableTags = this.data.tags;
    this.defaultTags = this.data.defaultTagIds.map(tagId => ({ id: tagId, name: this.allAvailableTags.find(tag => tag.id === tagId).name }));
    this.uploadDocumentForm = this.formBuilder.group({
      document_name: ['', Validators.required],
      tags: [''],
      custom_tags: [[]],
      expiry_date: [null],
      document: ['', Validators.required],
    }, { validator: atLeastOneTagValidator() });

    if (this.data?.defaultTitle) {
      this.uploadDocumentForm.patchValue({ document_name: this.data.defaultTitle });
    }

    if (this.data.options?.disableDocumentTitleInUploadDialog) {
      this.uploadDocumentForm.get('document_name').disable();
    } else {
      this.uploadDocumentForm.get('document_name').enable();
    }

    if (this.data.options?.disableTagAutocompleteInUploadDialog) {
      this.data.options.tagsAutocompleteOptions = { ...this.data.options.tagsAutocompleteOptions, disabled: true }
    }
  }

  uploadFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file.size > MAX_FILE_SIZE) {
      this.alertService.notifyError('Sorry, the file you tried to upload was too large. Please try again.');

      this.resetFile(input);
      return;
    }
    this.uploadDocumentForm.patchValue({ document: file });
  }

  resetFile(input: HTMLInputElement | undefined = undefined) {
    this.uploadDocumentForm.patchValue({ document: '' });
    if (input) {
      input.value = '';
    }
  }

  onSelectedTagsChanged(selectedTags: AutocompleteTag[]): void {
    const tags = selectedTags.filter(tag => !tag?.custom).map(tag => tag.id).join(',');
    const custom_tags = selectedTags.filter(tag => tag?.custom).map(tag => ({ name: tag.name }));
    this.uploadDocumentForm.patchValue({ tags, custom_tags })
  }

  uploadDocument() {

    if (this.uploadDocumentForm.valid) {
      this.loading = true;
      const data = {
        ...this.uploadDocumentForm.getRawValue(),
        user_id: this.data.userId,
      }
      if (this.data.orderId) {
        data.order_id = this.data.orderId;
      }

      if (data.expiry_date) {
        data.expiry_date = moment(data.expiry_date).format('DD/MM/YYYY');
      }

      // Add override flag if admin is overriding verification
      if (this.allowVerificationOverride && this.isAdmin) {
        data.skip_ocr_check = true;
      }

      this.documentService.createDocument(data).subscribe(
        () => {
          this.loading = false;
          this.uploadSuccess = true;
          this.verificationErrors = [];
          this.verificationExpectedData = null;
          this.verificationMatches = null;
          this.unsupportedFormatError = null;
        },
        (error) => {
          this.loading = false;

          // Handle document verification failure (status 422)
          if (error?.status === 422 && error?.error) {
            const errorData = error.error;

            // Check if this is an unsupported format error
            if (errorData.msg && errorData.msg.includes('document format is not supported')) {
              this.unsupportedFormatError = errorData.msg;
              this.verificationErrors = [];
              this.verificationExpectedData = null;
              this.verificationMatches = null;
              this.alertService.notifyError('Unsupported document format. Please upload as image or different PDF.');
            }
            // Check if this is a document verification error
            else if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
              this.verificationErrors = errorData.errors;
              this.verificationExpectedData = errorData.expected_data || null;
              this.verificationMatches = errorData.matches || null;
              this.unsupportedFormatError = null;

              // Show a summary alert
              const errorMessage = errorData.msg || 'Document verification failed';
              this.alertService.notifyError(errorMessage);
            } else {
              // Generic 422 error
              this.unsupportedFormatError = null;
              this.alertService.notifyError(error?.error?.message || error?.error?.msg || 'Failed to upload document. Please try again.');
            }
          } else {
            // Other errors
            this.unsupportedFormatError = null;
            this.alertService.notifyError(error?.error?.message || 'Failed to upload document. Please try again.');
          }
        }
      );
    } else {
      this.uploadDocumentForm.markAllAsTouched();
    }
  }

  isFieldHasRequiredError(fieldName: string): boolean {
    const control = this.uploadDocumentForm.get(fieldName);
    return control?.errors?.required && control?.touched;
  }

  resetFormForNextUpload(): void {
    this.uploadSuccess = false;
    this.verificationErrors = [];
    this.verificationExpectedData = null;
    this.verificationMatches = null;
    this.unsupportedFormatError = null;
    this.allowVerificationOverride = false;
    this.uploadDocumentForm.reset();
    this.uploadDocumentForm.patchValue({ tags: '', custom_tags: [] });
    if (this.data?.defaultTitle) {
      this.uploadDocumentForm.patchValue({ document_name: this.data.defaultTitle });
    }
  }

  overrideVerificationAndUpload(): void {
    this.allowVerificationOverride = true;
    this.verificationErrors = [];
    this.verificationExpectedData = null;
    this.verificationMatches = null;
    this.uploadDocument();
  }

}
